import { AfterViewInit, booleanAttribute, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, ElementRef, inject, input, output, viewChild } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideChevronLeft, lucideChevronRight, lucideImagePlus, lucideTrash2 } from '@ng-icons/lucide';
import type { ClassValue } from 'clsx';
import { mergeClasses } from '@solvace/zard/shared/utils/merge-classes';
import { ZardButtonComponent } from '../../app/shared/components/button/button.component';
import { SvzMediaCarouselAddEvent, SvzMediaCarouselRemoveEvent } from './interfaces/svz-media-carousel-events';
import { SvzMediaCarouselInternalItem, SvzMediaCarouselItem } from './interfaces/svz-media-carousel-item';
import { SvzMediaCarouselRenderMode } from './interfaces/svz-media-carousel-render-mode';

// AB#73407 — plain <button>, no z-button: the nav circle is a fixed, one-off shape (26px, translucent
// dark, hover-revealed) that doesn't belong to any zType variant, and routing it through z-button's own
// `class` input means merging against buttonVariants()'s base classes on every render — one more place a
// twMerge/tailwind-merge conflict-group mismatch could silently drop the override. Plain markup removes
// that whole class of risk for two buttons with no reason to share the general button component's API.
const NAV_BUTTON_BASE_CLASSES = 'absolute top-1/2 flex h-[26px] w-[26px] -translate-y-1/2 items-center justify-center rounded-full border-0 p-0 text-white transition-opacity';
const NAV_BUTTON_HOVER_CLASSES = 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100';

const DEFAULT_HEIGHT_PX = 150;
const RATIO_FOR_WIDTH = 1.8;
const GAP_BETWEEN_ITEMS_PX = 8;

@Component({
  selector: 'svz-media-carousel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './svz-media-carousel.component.html',
  viewProviders: [provideIcons({ lucideChevronLeft, lucideChevronRight, lucideImagePlus, lucideTrash2 })],
  standalone: true,
  imports: [ZardButtonComponent, NgIcon],
})
export class SvzMediaCarouselComponent implements AfterViewInit {
  protected readonly viewportRef = viewChild<ElementRef<HTMLElement>>('viewport');

  readonly itemsInput = input<SvzMediaCarouselItem[] | null | undefined>(undefined, { alias: 'items' });
  readonly heightInput = input<number | null | undefined>(undefined, { alias: 'height' });
  readonly renderModeInput = input<SvzMediaCarouselRenderMode | null | undefined>(undefined, { alias: 'renderMode' });
  readonly editModeInput = input<boolean | null | undefined>(undefined, { alias: 'editMode' });

  readonly maxItems = input(0);

  readonly videoControls = input(true);

  readonly playVideoWhenMouseHover = input(true);

  readonly initialItem = input<SvzMediaCarouselItem | null>(null);

  readonly goToEmptyViewWhenEditing = input(true);

  readonly mustCenterEmptyViewWhenAlone = input(true);

  readonly canClickToExpandImage = input(true);

  readonly emptyView = input(true);

  readonly isDark = input(false);

  readonly fitContent = input<'contain' | 'cover' | 'fill' | 'none' | 'scale-down'>('cover');

  /**
   * Fills the host's height instead of the fixed `height` in px, so a parent that sets a ratio
   * (`aspect-[16/9]`) governs the media box. Default `false` keeps the px sizing. AB#73414.
   */
  readonly fillHeight = input(false, { transform: booleanAttribute });

  readonly showEmptyInViewMode = input(true);

  // AB#73407 — additive. `true` preserves the original filename-caption overlay (useful in an editing/
  // upload context). A read-only search-result card has no such caption in its prototype — the dots
  // already communicate "which photo" — so RCA opts out.
  readonly showItemCaption = input(true, { transform: booleanAttribute });

  // AB#73407 — additive. 'add' preserves the original behavior (an "Add media" affordance shown
  // whenever there are no items, in view or edit mode alike). 'static' is for a read-only consumer
  // (e.g. a search-result card) that never adds media: it shows a plain, non-interactive placeholder
  // instead, sourced from emptyImageUrl/emptyBackgroundColor (both no-op when unset).
  readonly emptyMode = input<'add' | 'static'>('add');
  readonly emptyImageUrl = input<string | null>(null);
  readonly emptyBackgroundColor = input<string | null>(null);

  // AB#73407 — additive. 'always' preserves the original always-visible nav buttons. 'hover' matches
  // the RCA/Alerts prototype's own card-carousel rule (nav hidden until the ancestor `.card` is
  // hovered/focused) — the consumer must wrap this component in an element carrying Tailwind's `group`
  // class for the reveal to work.
  readonly navVisibility = input<'always' | 'hover'>('always');

  // AB#73407 — additive. Lets a consumer whose own container already owns the rounding/border/overflow
  // (e.g. RCA's card, clipped by its own 14px-radius `svz-card`) drop this component's own per-slide
  // `rounded-xl border bg-muted/20` so the photo fills edge-to-edge instead of floating as its own
  // separate rounded box inside an already-rounded card.
  readonly itemClass = input<ClassValue>('');

  // AB#73407 — additive. Default 7 matches the prototype's own `.card-dots{bottom:7px}`, calibrated for
  // a full-bleed slide. A consumer that also insets its own slide content via `itemClass` padding (RCA's
  // framed real-photo round) needs the dots to move up with it, or they end up floating in the empty
  // background band below the visible frame instead of hugging its edge.
  readonly dotsBottomPx = input(7);

  /** Additive. `light` keeps the white dots over photos; `dark` is the prototype `.alert-card .card-dots`: `rgba(17,24,39,.22)`
   *  dots without shadow and a primary active dot, readable on the `#eef1fb` preview band. */
  readonly dotTone = input<'light' | 'dark'>('light');

  protected readonly activeDotClass = computed(() =>
    this.dotTone() === 'dark' ? 'h-[7px] w-[7px] rounded-full bg-primary' : 'h-[7px] w-[7px] rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.3)]'
  );

  protected readonly inactiveDotClass = computed(() =>
    this.dotTone() === 'dark' ? 'h-[6px] w-[6px] rounded-full bg-[rgba(17,24,39,0.22)]' : 'h-[6px] w-[6px] rounded-full bg-white/60 shadow-[0_1px_2px_rgba(0,0,0,0.3)]'
  );

  // AB#73407 — additive. The real `<img>`/`<video>` had no class override point at all (only the
  // surrounding `<article>` does, via `itemClass`) — a consumer that insets the slide content (RCA's
  // framing round) needs the frame's own rounded corners/border on the *media itself*, not the outer
  // article, or the border would trace the full slide instead of the smaller inset box the padding creates.
  readonly mediaClass = input<ClassValue>('');

  protected get imageClasses(): string {
    return mergeClasses('h-full w-full bg-muted', this.mediaClass());
  }

  protected get videoClasses(): string {
    return mergeClasses('h-full w-full bg-black', this.mediaClass());
  }

  readonly onCurrentItemChanged = output<SvzMediaCarouselItem | null>();

  readonly onAddTriggered = output<SvzMediaCarouselAddEvent>();

  readonly onDeleteTriggered = output<SvzMediaCarouselRemoveEvent>();

  readonly onVideoPlay = output<HTMLVideoElement>();

  readonly onVideoPause = output<void>();

  readonly onVideoEnd = output<void>();

  protected items: SvzMediaCarouselInternalItem[] = [];
  protected editMode = false;
  protected renderMode = SvzMediaCarouselRenderMode.LIST;
  protected current: SvzMediaCarouselInternalItem | null = null;
  protected itemHeight = DEFAULT_HEIGHT_PX;
  protected itemWidth = Math.round(DEFAULT_HEIGHT_PX * RATIO_FOR_WIDTH);
  protected viewportRemainingToStart = 0;
  protected viewportRemainingToEnd = 0;

  // AB#73407 — real defect, not a style choice: this component is OnPush, and the effect below only
  // mutated plain fields (`items`/`renderMode`/etc.) with no `markForCheck()`. OnPush only re-renders a
  // view on an input *reference* change, an event inside it, or a signal read directly in the template
  // — none of those apply to a plain field an effect assigns later, so the template kept showing
  // whatever it rendered on the very first pass (before the effect had run at all) forever after.
  // Invisible with zero real consumers and a spec that only ever asserted against the component
  // instance's own fields, never the rendered DOM. Would have silently broken RCA's card exactly the
  // way `svz-data-table`'s client-side mode did (ZARD-GAPS.md row 14): attachments loaded async, after
  // the card already mounted with `items` empty.
  readonly #cdr = inject(ChangeDetectorRef);

  private readonly syncInputs = effect(() => {
    this.items = [...(this.itemsInput() ?? [])];
    this.itemHeight = this.heightInput() ?? DEFAULT_HEIGHT_PX;
    this.renderMode = this.renderModeInput() ?? SvzMediaCarouselRenderMode.LIST;
    this.editMode = this.editModeInput() ?? false;
    this.ensureCurrentStillExists();
    this.recalculateItemSizes();
    this.refreshNavigationState();
    this.#cdr.markForCheck();
  });

  protected get renderModeIsList(): boolean {
    return this.renderMode === SvzMediaCarouselRenderMode.LIST;
  }

  protected get canShowEmptyView(): boolean {
    return this.emptyView() && (!this.items.length || (this.editMode && (this.maxItems() <= 0 || this.items.length < this.maxItems())));
  }

  protected get currentIndex(): number {
    return this.current ? this.items.indexOf(this.current) : -1;
  }

  // AB#73407 — dots need a definite active index even before the user has interacted (`current` is
  // `null` until the first click/nav, per `ngAfterViewInit`/`goToNext`), so they default to slide 0
  // like the prototype's own `imgIx || 0` — without changing what `current`/the ring highlight do.
  protected get activeDotIndex(): number {
    return Math.max(this.currentIndex, 0);
  }

  protected get showDots(): boolean {
    return !this.renderModeIsList && this.items.length > 1;
  }

  protected navButtonClasses(side: 'left-2' | 'right-2'): string {
    return mergeClasses(NAV_BUTTON_BASE_CLASSES, side, this.navVisibility() === 'hover' ? NAV_BUTTON_HOVER_CLASSES : '');
  }

  protected get itemArticleClasses(): string {
    return mergeClasses('group relative shrink-0 overflow-hidden rounded-xl border bg-muted/20', this.itemClass());
  }

  protected trackItem(index: number, item: SvzMediaCarouselItem): unknown {
    return item?.id ?? item?.url ?? index;
  }

  ngAfterViewInit(): void {
    this.recalculateItemSizes();
    this.refreshNavigationState();
    const initialItem = this.initialItem();
    if (initialItem) {
      this.setCurrent(initialItem, { emitEvent: false, animate: false });
    }
  }

  refresh(): SvzMediaCarouselItem[] {
    this.items = [...this.items];
    this.recalculateItemSizes();
    this.refreshNavigationState();
    return this.items;
  }

  goToPrevious(event?: Event): void {
    event?.stopPropagation();
    if (this.renderModeIsList) {
      this.viewportRef()?.nativeElement.scrollBy({
        left: -(this.itemWidth + GAP_BETWEEN_ITEMS_PX),
        behavior: 'smooth',
      });
      return;
    }

    if (this.currentIndex > 0) {
      this.setCurrent(this.items[this.currentIndex - 1]);
      return;
    }

    if (this.currentIndex === 0 && this.canShowEmptyView) {
      this.goToEmptyView();
    }
  }

  goToNext(event?: Event): void {
    event?.stopPropagation();
    if (this.renderModeIsList) {
      this.viewportRef()?.nativeElement.scrollBy({
        left: this.itemWidth + GAP_BETWEEN_ITEMS_PX,
        behavior: 'smooth',
      });
      return;
    }

    if (this.current == null && this.items.length) {
      this.setCurrent(this.items[0]);
      return;
    }

    if (this.currentIndex >= 0 && this.currentIndex < this.items.length - 1) {
      this.setCurrent(this.items[this.currentIndex + 1]);
    }
  }

  setCurrent(item: SvzMediaCarouselItem | null, options: { emitEvent?: boolean; animate?: boolean } = {}): boolean {
    if (!item && this.canShowEmptyView) {
      this.current = null;
      if (options.emitEvent !== false) {
        this.onCurrentItemChanged.emit(null);
      }
      this.scrollToIndex(0, options.animate ?? true);
      return true;
    }

    const internalItem = item ? this.getMatchingItemByIdOrUrl(item) : null;
    if (!internalItem) {
      return false;
    }

    this.current = internalItem;
    if (options.emitEvent !== false) {
      this.onCurrentItemChanged.emit(internalItem);
    }

    const index = this.items.indexOf(internalItem) + (this.canShowEmptyView ? 1 : 0);
    this.scrollToIndex(index, options.animate ?? true);
    return true;
  }

  goToEmptyView(): void {
    if (!this.canShowEmptyView) {
      return;
    }

    this.setCurrent(null);
  }

  getCurrent(): SvzMediaCarouselItem | null {
    return this.current;
  }

  getMatchingItemByIdOrUrl<T extends SvzMediaCarouselItem>(values: { id?: number | string; url?: string }): T | null {
    if (values?.url) {
      const byUrl = this.items.find((item) => item?.url === values.url);
      if (byUrl) {
        return byUrl as T;
      }
    }

    if (values?.id != null) {
      const byId = this.items.find((item) => item?.id === values.id);
      if (byId) {
        return byId as T;
      }
    }

    return null;
  }

  addItem(values: { item: SvzMediaCarouselItem; atBeginning?: boolean; beCurrent?: boolean }): SvzMediaCarouselItem[] {
    if (!values.item) {
      return this.items;
    }

    const existingItem = this.getMatchingItemByIdOrUrl(values.item);
    if (existingItem) {
      return this.items;
    }

    if (this.maxItems() > 0 && this.items.length >= this.maxItems()) {
      return this.items;
    }

    const nextItems = values.atBeginning ? [values.item as SvzMediaCarouselInternalItem, ...this.items] : [...this.items, values.item as SvzMediaCarouselInternalItem];

    this.items = nextItems;
    this.recalculateItemSizes();
    this.refreshNavigationState();

    if (values.beCurrent ?? true) {
      this.setCurrent(values.item, { animate: false });
    }

    return nextItems;
  }

  deleteItem(item: SvzMediaCarouselItem): SvzMediaCarouselItem[] {
    const existingItem = this.getMatchingItemByIdOrUrl(item);
    if (!existingItem) {
      return this.items;
    }

    const currentIndex = this.items.indexOf(existingItem);
    this.items = this.items.filter((entry) => entry !== existingItem);

    if (this.current === existingItem) {
      this.current = this.items[currentIndex] ?? this.items[currentIndex - 1] ?? null;
      this.onCurrentItemChanged.emit(this.current);
    }

    this.recalculateItemSizes();
    this.refreshNavigationState();
    return this.items;
  }

  openItem(item: SvzMediaCarouselItem, _options: { force?: boolean } = {}): void {
    this.setCurrent(item);
  }

  tryPlayCurrentItemVideo(): void {
    if (this.current?.type !== 'video') {
      return;
    }

    const video = this.viewportRef()?.nativeElement.querySelector<HTMLVideoElement>('video[data-current="true"]');
    video?.play();
  }

  protected onViewportScroll(): void {
    const viewport = this.viewportRef()?.nativeElement;
    if (!viewport) {
      return;
    }

    this.viewportRemainingToStart = viewport.scrollLeft;
    this.viewportRemainingToEnd = Math.max(viewport.scrollWidth - (viewport.offsetWidth + viewport.scrollLeft), 0);
  }

  protected onItemClick(item: SvzMediaCarouselInternalItem): void {
    this.setCurrent(item);
  }

  protected emitAddItem(): void {
    if (!this.canShowEmptyView) {
      return;
    }

    this.onAddTriggered.emit({
      onAdded: this.addItem.bind(this),
    });
  }

  protected emitDeleteItem(item: SvzMediaCarouselInternalItem, event?: Event): void {
    event?.stopPropagation();
    this.onDeleteTriggered.emit({
      item,
      onDeleted: () => this.deleteItem(item),
    });
  }

  protected resolveItemWidth(): number | null {
    return this.renderModeIsList ? this.itemWidth : null;
  }

  protected handleVideoPlay(event: Event): void {
    this.onVideoPlay.emit(event.target as HTMLVideoElement);
  }

  protected handleVideoPause(): void {
    this.onVideoPause.emit();
  }

  protected handleVideoEnd(): void {
    this.onVideoEnd.emit();
  }

  private ensureCurrentStillExists(): void {
    if (!this.current) {
      return;
    }

    this.current = this.getMatchingItemByIdOrUrl(this.current);
  }

  private recalculateItemSizes(): void {
    this.itemWidth = Math.round(this.itemHeight * RATIO_FOR_WIDTH);
  }

  private scrollToIndex(index: number, animate: boolean): void {
    const viewport = this.viewportRef()?.nativeElement;
    if (!viewport) {
      return;
    }

    // AB#73407 — real defect: each one-by-one slide is `w-full` (= viewport.offsetWidth) but the
    // viewport is a flex row with `gap-2` (8px) between slides, so the true distance from one slide's
    // left edge to the next is offsetWidth + 8px, not offsetWidth alone. Omitting the gap under-scrolled
    // by 8px per index, a cumulative drift that grew with every "next" click — by slide 5 the viewport
    // was ~32px short, revealing a sliver of the previous slide's own frame at the edge.
    const itemWidth = this.renderModeIsList ? this.itemWidth + GAP_BETWEEN_ITEMS_PX : viewport.offsetWidth + GAP_BETWEEN_ITEMS_PX;
    viewport.scrollTo({
      left: Math.max(index, 0) * itemWidth,
      behavior: animate ? 'smooth' : ('instant' as ScrollBehavior),
    });
  }

  private refreshNavigationState(): void {
    setTimeout(() => this.onViewportScroll());
  }
}
